import { Cart, DiscountCodeState } from "@commercetools/platform-sdk"
import { TFunction } from "i18next"
import { useTranslation } from "react-i18next"
import { CartService } from "../../../cart/CartService"
import {
  success,
  useValidationStrategy,
  CartValidationBase,
  cartSuccess
} from "../../validation/FormValidator"
import {
  error,
  ValidationResult,
  ValidationError,
  CartValidationResult,
  cartError
} from "../../validation/ValidationError"
import { ErrorType } from "../../../error/ErrorType"
import { useCartService } from "../../../cart/CartServiceHook"
import { Either } from "fp-ts/Either"

export const invalidDiscountCodeStates: DiscountCodeState[] = [
  "NotActive",
  "NotValid",
  "MaxApplicationReached"
]

export const useDiscountCodeValidationForCheckout = (
  cart: Cart | undefined
) => {
  const { t } = useTranslation("checkout")

  const createError = (invalidDiscountCodes: string[]) =>
    error(ErrorType.CartValidationError, {
      message: t("discountCodeNoLongerValid", {
        invalidDiscountCodes: invalidDiscountCodes.toString()
      })
    })

  return useDiscountCodeValidation(cart, createError)
}

export const useDiscountCodeValidationForPaymentValidation = (
  cart: Cart | undefined
) => {
  const { t } = useTranslation("checkout")

  const createError = (invalidDiscountCodes: string[]) =>
    error(ErrorType.PaymentError, {
      message: t("discountCodeNoLongerValid", {
        invalidDiscountCodes: invalidDiscountCodes.toString()
      })
    })

  return useDiscountCodeValidation(cart, createError)
}

const useDiscountCodeValidation = (
  cart: Cart | undefined,
  createError: (
    invalidDiscountCodes: string[]
  ) => Promise<Either<ValidationError, void>>
) => {
  const cartService = useCartService()
  let c = cart
  return useValidationStrategy(() => true, {
    name: "DiscountCodeValidation",
    refreshCart: newCart => (c = newCart),
    validate(): ValidationResult {
      const invalidCartDiscounts =
        c?.discountCodes?.filter(it =>
          invalidDiscountCodeStates.includes(it.state)
        ) || []
      if (invalidCartDiscounts.length > 0) {
        invalidCartDiscounts.forEach(it =>
          cartService.removeDiscountCode(it.discountCode)
        )

        return createError(
          invalidCartDiscounts.map(it =>
            it.discountCode.obj?.code ? it.discountCode.obj?.code : ""
          )
        )
      } else {
        return success()
      }
    },
    save(): ValidationResult {
      return success()
    }
  })
}

export class DiscountCodeValidation extends CartValidationBase {
  private readonly t: TFunction
  private cartService: CartService
  constructor(t: TFunction, cartService: CartService) {
    super("DiscountCodeValidation")
    this.t = t
    this.cartService = cartService
  }

  save(c: Cart): CartValidationResult {
    const invalidCartDiscounts =
      c.discountCodes?.filter(it =>
        invalidDiscountCodeStates.includes(it.state)
      ) || []
    if (invalidCartDiscounts.length === 0) {
      return cartSuccess(c)
    }
    invalidCartDiscounts.forEach(it =>
      this.cartService.removeDiscountCode(it.discountCode)
    )

    const invalidDiscountCodes = invalidCartDiscounts.map(it =>
      it.discountCode.obj?.code ? it.discountCode.obj?.code : ""
    )

    return cartError(ErrorType.PaymentError, {
      message: this.t("checkout:discountCodeNoLongerValid", {
        invalidDiscountCodes: invalidDiscountCodes.toString()
      })
    })
  }
}
